import { Component, computed, effect, inject, signal, ChangeDetectionStrategy } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { DateInputComponent } from '../../../../../../components/kit/input/date-input/date-input.component';
import { MultiSelectInputComponent } from '../../../../../../components/kit/input/multi-select-input/multi-select-input.component';
import { SelectInputComponent } from '../../../../../../components/kit/input/select-input/select-input.component';
import {
    TextAreaInputComponent,
} from '../../../../../../components/kit/input/text-area-input/text-area-input.component';
import { FileKindEnum, fileKindEnumOptions } from '@libs/types/src/file-manager/enum/file-kind.enum';
import { CategoryGridItemObject } from '@it2go/types';
import { DefaultCategoryTypeEnum } from '@libs/types/src/file-manager/types/default-category.type';
import { FilesManagerPageStore } from '../../files-manager/files-manager.store';
import { BaseDialogComponent } from '../../../../../../components/kit/base-dialog/base-dialog.component';
import { getAllowedFileCategories } from '../../../../../../utils/enum/file-enum.utils';
import { FileMetadataDialogStore } from './file-metadata-dialog.store';
import { ExternalType } from '@libs/types/src/file-manager/enum/external-type.enum';
import { isInvalid } from '../../../../../shared/helpers/validate';
import { OrganizationSettingTypeEnum } from '@libs/types/src/organization/enum/organization-setting-type.enum';
import { FilterBuilder } from '../../../../helper/filter.builder';

@Component({
    selector: 'app-file-metadata-dialog',
    templateUrl: './file-metadata-dialog.component.html',
    styleUrl: './file-metadata-dialog.component.scss',
    providers: [FileMetadataDialogStore, FilesManagerPageStore],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        DateInputComponent,
        MultiSelectInputComponent,
        SelectInputComponent,
        TextAreaInputComponent,
    ],
})
export class FileMetadataDialogComponent extends BaseDialogComponent {

    private readonly fb = inject(FormBuilder);
    protected readonly store = inject(FilesManagerPageStore);
    protected readonly store2 = inject(FileMetadataDialogStore);

    protected folderId = signal<any>(null);
    protected allowed = computed(() => {
        return getAllowedFileCategories(this.store2, {
            serviceId: this.folderId() ? Number(this.store2.globalGlobalStore.loggedUserStore.selectedServiceId() || 0) : undefined,
        });
    });

    protected categories = computed(() => this.store.data.getCategories()?.items || []);

    form = this.fb.group({
        date: this.fb.control<Date>(new Date()),
        kind: this.fb.control<FileKindEnum | null>(FileKindEnum.received),
        note: this.fb.control<string | null>(null),
        categoryId: this.fb.control<number | null>(null, Validators.required),
        serviceIds: this.fb.control<number[]>([]),
    });

    protected filterCategories = signal([]);

    constructor() {
        super();
        this.folderId.set(this.dialogConfig.data.folderId);

        effect(() => {
            this.folderId();
            this.store2.globalGlobalStore.organizationStore.serviceInContextIds();
            this.fetchCategories();
        });

        effect(() => {
            const allowed = this.allowed();
            const cats = this.categories();
            const defaults = Object.values(DefaultCategoryTypeEnum);
            const res = (cats || [])
                .filter((cat: CategoryGridItemObject) => {
                    if (cat.code && defaults.includes(cat.code as any)) {
                        return allowed.includes(cat.code);
                    }
                    return true;
                })
                .map((cat: CategoryGridItemObject) => {
                    if (cat.code === DefaultCategoryTypeEnum.DEFAULT) {
                        this.form.patchValue({ categoryId: cat.id }, { emitEvent: false });
                        return {
                            ...cat,
                            name: 'Ostatní', // quick fix
                        };
                    }
                    return cat;
                });
            this.filterCategories.set(res);
        })

        effect(() => {
            if (this.store2.globalGlobalStore.organizationStore.isSettingActive(OrganizationSettingTypeEnum.HEALTHCARE_SOCIAL_SETTINGS)() && this.store2.isAllowed('service.read')()) {
                const sId = this.store2.globalGlobalStore.loggedUserStore.selectedServiceId();
                if (!sId) return;
                this.form.patchValue({ serviceIds: [sId] });
            }
        });
    }

    override save(): void {
        if (isInvalid(this.form)) return;

        this.dialogRef.close({
            ...this.form.value,
            accesses: (this.form.value.serviceIds || []).map((it) => {
                return { externalType: ExternalType.service, externalId: String(it) };
            }),
        });
    }

    private fetchCategories(): void {
        const filter = new FilterBuilder().limit(100);
        filter.where('active', true);
        if (this.store2.globalGlobalStore.organizationStore.serviceInContextIds().length > 0) {
            filter.where('serviceId', this.store2.globalGlobalStore.organizationStore.serviceInContextIds());
        }
        // if (this.store2.globalGlobalStore.organizationStore.serviceIds().length > 0) {
        //     filter.where('serviceId', this.store2.globalGlobalStore.organizationStore.serviceIds());
        // }
        if (this.folderId()) {
            filter.folderId(this.folderId()!);
        }

        this.store.api.getCategories(filter.filter());
    }

    protected readonly fileKindEnumOptions = fileKindEnumOptions;
    protected readonly OrganizationSettingTypeEnum = OrganizationSettingTypeEnum;
}
